import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  adminGetBookings, adminDeleteBooking, adminGetBlackouts,
  adminAddBlackout, adminRemoveBlackout, logout, getReviews, adminDeleteReview,
} from "../../lib/api";
import { IMAGES, BRAND } from "../../lib/brand";
import { AuthImage } from "../auth/AuthImage";
import {
  LogOut, Calendar as CalIcon, Phone, MapPin, Mail, Trash2, Loader2,
  CalendarOff, Package, Clock, ExternalLink, Star, MessageSquare,
} from "lucide-react";

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

export const Dashboard = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [blackouts, setBlackouts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("upcoming");
  const [newBlackout, setNewBlackout] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([adminGetBookings(), adminGetBlackouts(), getReviews()])
      .then(([b, bl, rv]) => { setBookings(b); setBlackouts(bl); setReviews(rv); })
      .catch(() => toast.error("Couldn't load dashboard data."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const today = todayISO();
  const upcoming = useMemo(() => bookings.filter((b) => b.date >= today), [bookings, today]);
  const past = useMemo(() => bookings.filter((b) => b.date < today), [bookings, today]);
  const list = tab === "upcoming" ? upcoming : past;

  const doLogout = async () => { await logout(); navigate("/login"); };

  const removeBooking = async (id) => {
    try { await adminDeleteBooking(id); toast.success("Booking removed."); load(); }
    catch { toast.error("Couldn't remove booking."); }
  };

  const addBlackout = async () => {
    if (!newBlackout) { toast.error("Pick a date to block."); return; }
    try { await adminAddBlackout(newBlackout); toast.success(`Blocked ${newBlackout}.`); setNewBlackout(""); load(); }
    catch { toast.error("Couldn't block that date."); }
  };
  const removeBlackout = async (d) => {
    try { await adminRemoveBlackout(d); toast.success(`Reopened ${d}.`); load(); }
    catch { toast.error("Couldn't reopen that date."); }
  };
  const removeReview = async (id) => {
    try { await adminDeleteReview(id); toast.success("Review removed."); load(); }
    catch { toast.error("Couldn't remove review."); }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="sticky top-0 z-40 bg-black/95 backdrop-blur-md border-b-2 border-[#2d2d2d]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={IMAGES.logo} alt={BRAND.name} className="h-11 w-11 object-contain" />
            <span className="font-display uppercase text-lg leading-none">Owner<br /><span className="text-[#7CB518]">Dashboard</span></span>
          </div>
          <div className="flex items-center gap-3">
            <a href="/" className="hidden sm:inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-white/60 hover:text-[#7CB518]">
              View Site <ExternalLink size={14} />
            </a>
            <button data-testid="logout-btn" onClick={doLogout}
              className="press inline-flex items-center gap-2 border-2 border-[#2d2d2d] hover:border-[#7CB518] px-4 py-2 text-sm uppercase font-display">
              <LogOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Stat blocks */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border-2 border-[#2d2d2d] mb-8">
          {[
            { label: "Upcoming", value: upcoming.length, icon: CalIcon },
            { label: "Total Jobs", value: bookings.length, icon: Package },
            { label: "Completed", value: past.length, icon: Clock },
            { label: "Reviews", value: reviews.length, icon: MessageSquare },
          ].map((s, i) => (
            <div key={i} className="p-6 border-r-2 last:border-r-0 border-[#2d2d2d] odd:border-b-2 lg:border-b-0 even:border-b-2 lg:even:border-b-0">
              <s.icon className="text-[#7CB518] mb-2" size={22} />
              <div className="font-display text-4xl">{loading ? "—" : s.value}</div>
              <div className="text-xs uppercase tracking-[0.2em] text-white/50">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Bookings */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2 mb-4">
              {["upcoming", "past"].map((t) => (
                <button key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
                  className={["press px-5 py-2 font-display uppercase text-sm border-2",
                    tab === t ? "bg-[#7CB518] text-black border-[#7CB518]" : "border-[#2d2d2d] text-white/70"].join(" ")}>
                  {t} ({t === "upcoming" ? upcoming.length : past.length})
                </button>
              ))}
            </div>

            {loading ? (
              <div className="h-40 grid place-items-center"><Loader2 className="animate-spin text-[#7CB518]" /></div>
            ) : list.length === 0 ? (
              <div className="border-2 border-dashed border-[#2d2d2d] p-10 text-center text-white/40 uppercase tracking-widest text-sm">
                No {tab} bookings
              </div>
            ) : (
              <div className="space-y-4">
                {list.map((b, i) => (
                  <motion.div key={b.id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.03 }} data-testid={`booking-row-${i}`}
                    className="border-2 border-[#2d2d2d] bg-[#0d0d0d] p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 flex-wrap">
                          <span className="font-display uppercase text-xl">{b.name}</span>
                          <span className="text-xs bg-[#7CB518] text-black px-2 py-0.5 uppercase font-bold">{b.load_size}</span>
                        </div>
                        <div className="mt-2 grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm text-white/70">
                          <span className="flex items-center gap-2"><CalIcon size={14} className="text-[#7CB518]" /> {b.date} @ {b.time_slot}</span>
                          <a href={`tel:${b.phone}`} className="flex items-center gap-2 hover:text-[#7CB518]"><Phone size={14} className="text-[#7CB518]" /> {b.phone}</a>
                          <span className="flex items-center gap-2"><MapPin size={14} className="text-[#7CB518]" /> {b.address}</span>
                          {b.email && <a href={`mailto:${b.email}`} className="flex items-center gap-2 hover:text-[#7CB518] truncate"><Mail size={14} className="text-[#7CB518]" /> {b.email}</a>}
                        </div>
                        {b.notes && <p className="mt-2 text-sm text-white/50 italic">"{b.notes}"</p>}
                        {b.photos?.length > 0 && (
                          <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2 max-w-md">
                            {b.photos.map((p) => <AuthImage key={p.id} path={p.path} alt="junk" />)}
                          </div>
                        )}
                      </div>
                      <button data-testid={`delete-booking-${i}`} onClick={() => removeBooking(b.id)}
                        className="press shrink-0 border-2 border-[#2d2d2d] hover:border-red-500 hover:text-red-500 p-2" title="Remove booking">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* Blackout dates */}
          <div>
            <div className="border-2 border-[#7CB518] bg-[#0d0d0d] p-6 lg:sticky lg:top-24">
              <h3 className="font-display uppercase text-2xl flex items-center gap-2"><CalendarOff size={22} className="text-[#7CB518]" /> Block Days Off</h3>
              <p className="text-xs text-white/50 mt-2 mb-4">Close off holidays or busy days so customers can't book them.</p>
              <div className="flex gap-2">
                <input data-testid="blackout-date-input" type="date" min={today} value={newBlackout}
                  onChange={(e) => setNewBlackout(e.target.value)} className="brutal-input" />
                <button data-testid="add-blackout-btn" onClick={addBlackout}
                  className="press bg-[#7CB518] text-black font-display uppercase px-4 border-2 border-[#7CB518]">Block</button>
              </div>

              <div className="mt-5 space-y-2 max-h-72 overflow-auto">
                {blackouts.length === 0 ? (
                  <p className="text-xs text-white/30 uppercase tracking-widest">No days blocked</p>
                ) : blackouts.map((bl, i) => (
                  <div key={bl.date} data-testid={`blackout-${i}`} className="flex items-center justify-between border-2 border-[#2d2d2d] px-3 py-2">
                    <span className="text-sm">{bl.date}</span>
                    <button data-testid={`remove-blackout-${i}`} onClick={() => removeBlackout(bl.date)}
                      className="press text-white/50 hover:text-red-500"><Trash2 size={15} /></button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Manage reviews */}
        <div className="mt-12">
          <h2 className="font-display uppercase text-2xl md:text-3xl flex items-center gap-2 mb-4">
            <MessageSquare className="text-[#7CB518]" size={24} /> Manage Reviews ({reviews.length})
          </h2>
          {loading ? (
            <div className="h-32 grid place-items-center"><Loader2 className="animate-spin text-[#7CB518]" /></div>
          ) : reviews.length === 0 ? (
            <div className="border-2 border-dashed border-[#2d2d2d] p-10 text-center text-white/40 uppercase tracking-widest text-sm">No reviews yet</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {reviews.map((r, i) => (
                <div key={r.id} data-testid={`review-manage-${i}`} className="border-2 border-[#2d2d2d] bg-[#0d0d0d] p-5 flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <Star key={s} size={14} fill={s <= r.rating ? "#7CB518" : "none"} stroke={s <= r.rating ? "#7CB518" : "#555"} />
                      ))}
                    </div>
                    <button data-testid={`delete-review-${i}`} onClick={() => removeReview(r.id)}
                      className="press shrink-0 border-2 border-[#2d2d2d] hover:border-red-500 hover:text-red-500 p-1.5" title="Remove review">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <p className="text-sm text-white/85 mt-3 flex-1">"{r.text}"</p>
                  <div className="mt-3 pt-3 border-t-2 border-[#2d2d2d] text-xs uppercase tracking-widest text-white/50">
                    {r.name}{r.location ? ` · ${r.location}` : ""}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
